import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { SuperAdminSelfResponse } from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import {
  addSuperAdminPlatformAdmin,
  getSuperAdminConfig,
  getSuperAdminDashboard,
  getSuperAdminMe,
  getSuperAdminOrganization,
  getSuperAdminSettings,
  getSuperAdminUser,
  listSuperAdminAuditLogs,
  listSuperAdminOrganizations,
  listSuperAdminPlatformAdmins,
  listSuperAdminUserSessions,
  listSuperAdminUsers,
  removeSuperAdminPlatformAdmin,
  revokeAllSuperAdminUserSessions,
  revokeSuperAdminUserSession,
  updateSuperAdminConfig,
  updateSuperAdminOrganizationBeta,
  updateSuperAdminOrganizationEntitlement,
  updateSuperAdminOrganizationStatus,
  updateSuperAdminSettings,
} from "../../api/super-admin-api.js";
import { AccessDeniedPage, NotFoundPage } from "../../components/common/RouteStatus/index.js";
import { AppBootstrapLoader } from "../../components/layout/AppBootstrapLoader.js";
import { SuperAdminShell } from "./SuperAdminShell.js";
import { SuperAdminDashboardView, type SuperAdminLoadState } from "./SuperAdminDashboardPage.js";
import { SuperAdminOrganizationsView } from "./SuperAdminOrganizationsPage.js";
import { SuperAdminOrganizationDetailView } from "./SuperAdminOrganizationDetailPage.js";
import { SuperAdminBetaFeaturesPage } from "./SuperAdminBetaFeaturesPage.js";
import { SuperAdminSettingsView, type SuperAdminSettingsData } from "./SuperAdminSettingsPage.js";
import { SuperAdminUsersView } from "./SuperAdminUsersPage.js";
import { SuperAdminUserDetailView, type SuperAdminUserDetailData } from "./SuperAdminUserDetailPage.js";
import { SuperAdminTeamSection } from "./SuperAdminTeamSection.js";
import {
  EMPTY_SUPER_ADMIN_LOG_FILTERS,
  SuperAdminLogsView,
  isInvalidLogDateRange,
  type SuperAdminLogFilters,
  type SuperAdminLogScope,
} from "./SuperAdminLogsPage.js";
import { localDayEndExclusiveIso, localDayStartIso } from "./super-admin-audit-display.js";
import { SUPER_ADMIN_SETTINGS_DEFAULT_PATH } from "./super-admin-settings-nav.js";
import {
  SUPER_ADMIN_TABLE_DEFAULT_PAGE_SIZE,
  type SuperAdminTablePageSize,
} from "./super-admin-table-presentation.js";

export type SuperAdminGateState =
  | { status: "loading" }
  | { status: "denied" }
  | { status: "ready"; admin: SuperAdminSelfResponse };

export function SuperAdminApp({ onLogout }: { onLogout: () => void }): React.JSX.Element {
  const [gate, setGate] = useState<SuperAdminGateState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    getSuperAdminMe(controller.signal).then(
      (admin) => {
        if (!controller.signal.aborted) setGate({ status: "ready", admin });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        if (isApiError(error) && error.status === 401) {
          onLogout();
          return;
        }
        setGate({ status: "denied" });
      },
    );
    return () => controller.abort();
  }, [onLogout]);

  return <SuperAdminGate gate={gate} onLogout={onLogout} />;
}

export function SuperAdminGate({
  gate,
  onLogout,
}: {
  gate: SuperAdminGateState;
  onLogout: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (gate.status === "loading") {
    return <AppBootstrapLoader label={t("app.loading")} />;
  }
  if (gate.status === "denied") {
    return <AccessDeniedPage homePath="/inbox" />;
  }

  return (
    <Routes>
      <Route path="/super-admin" element={<SuperAdminShell admin={gate.admin} onLogout={onLogout} />}>
        <Route index element={<DashboardRoute />} />
        <Route path="organizations" element={<OrganizationsRoute />} />
        <Route path="organizations/:organizationId/*" element={<OrganizationDetailRoute />} />
        <Route path="beta-features" element={<SuperAdminBetaFeaturesPage />} />
        <Route path="users" element={<UsersRoute />} />
        <Route
          path="users/:userId"
          element={<UserDetailRoute currentUserId={gate.admin.userId} onLogout={onLogout} />}
        />
        <Route path="logs" element={<LogsRoute />} />
        <Route path="settings" element={<Navigate to={SUPER_ADMIN_SETTINGS_DEFAULT_PATH} replace />} />
        <Route path="settings/*" element={<SettingsRoute currentUserId={gate.admin.userId} />} />
        <Route path="*" element={<NotFoundPage homePath="/super-admin" />} />
      </Route>
    </Routes>
  );
}

function DashboardRoute(): React.JSX.Element {
  const [state, setState] = useState<SuperAdminLoadState<Awaited<ReturnType<typeof getSuperAdminDashboard>>>>({
    status: "loading",
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    getSuperAdminDashboard(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: "ready", data });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [attempt]);

  return <SuperAdminDashboardView state={state} onRetry={() => setAttempt((value) => value + 1)} />;
}

function OrganizationsRoute(): React.JSX.Element {
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useState<SuperAdminTablePageSize>(SUPER_ADMIN_TABLE_DEFAULT_PAGE_SIZE);
  const [state, setState] = useState<
    SuperAdminLoadState<Awaited<ReturnType<typeof listSuperAdminOrganizations>>>
  >({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    listSuperAdminOrganizations(
      { search: submittedSearch || undefined, cursor, limit: pageSize },
      controller.signal,
    ).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: "ready", data });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [submittedSearch, cursor, pageSize, attempt]);

  return (
    <SuperAdminOrganizationsView
      state={state}
      search={search}
      pageSize={pageSize}
      isFirstPage={cursor === undefined}
      onSearchChange={setSearch}
      onSearchSubmit={() => {
        setCursor(undefined);
        setSubmittedSearch(search.trim());
      }}
      onPageSizeChange={(size) => {
        setCursor(undefined);
        setPageSize(size);
      }}
      onNextPage={() => {
        if (state.status === "ready" && state.data.nextCursor) {
          setCursor(state.data.nextCursor);
        }
      }}
      onFirstPage={() => setCursor(undefined)}
      onRetry={() => setAttempt((value) => value + 1)}
    />
  );
}

function UsersRoute(): React.JSX.Element {
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useState<SuperAdminTablePageSize>(SUPER_ADMIN_TABLE_DEFAULT_PAGE_SIZE);
  const [state, setState] = useState<SuperAdminLoadState<Awaited<ReturnType<typeof listSuperAdminUsers>>>>({
    status: "loading",
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    listSuperAdminUsers(
      { search: submittedSearch || undefined, cursor, limit: pageSize },
      controller.signal,
    ).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: "ready", data });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [submittedSearch, cursor, pageSize, attempt]);

  return (
    <SuperAdminUsersView
      state={state}
      search={search}
      pageSize={pageSize}
      isFirstPage={cursor === undefined}
      onSearchChange={setSearch}
      onSearchSubmit={() => {
        setCursor(undefined);
        setSubmittedSearch(search.trim());
      }}
      onPageSizeChange={(size) => {
        setCursor(undefined);
        setPageSize(size);
      }}
      onNextPage={() => {
        if (state.status === "ready" && state.data.nextCursor) {
          setCursor(state.data.nextCursor);
        }
      }}
      onFirstPage={() => setCursor(undefined)}
      onRetry={() => setAttempt((value) => value + 1)}
    />
  );
}

function parsePositiveIntParam(value: string | null): number | undefined {
  if (value === null || !/^[1-9][0-9]*$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
}

function LogsRoute(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationId = parsePositiveIntParam(searchParams.get("organizationId"));
  const userId = parsePositiveIntParam(searchParams.get("userId"));
  const scope: SuperAdminLogScope | null =
    organizationId !== undefined
      ? { kind: "organization", id: organizationId }
      : userId !== undefined
        ? { kind: "user", id: userId }
        : null;
  const [filters, setFilters] = useState<SuperAdminLogFilters>(EMPTY_SUPER_ADMIN_LOG_FILTERS);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useState<SuperAdminTablePageSize>(SUPER_ADMIN_TABLE_DEFAULT_PAGE_SIZE);
  const [state, setState] = useState<SuperAdminLoadState<Awaited<ReturnType<typeof listSuperAdminAuditLogs>>>>({
    status: "loading",
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (isInvalidLogDateRange(filters)) return;
    const controller = new AbortController();
    setState({ status: "loading" });
    listSuperAdminAuditLogs(
      {
        cursor,
        limit: pageSize,
        category: filters.category === "all" ? undefined : filters.category,
        actorKind: filters.actorKind === "all" ? undefined : filters.actorKind,
        organizationId,
        userId: organizationId === undefined ? userId : undefined,
        from: localDayStartIso(filters.from),
        to: localDayEndExclusiveIso(filters.to),
      },
      controller.signal,
    ).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: "ready", data });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [filters, cursor, pageSize, organizationId, userId, attempt]);

  return (
    <SuperAdminLogsView
      state={state}
      filters={filters}
      scope={scope}
      pageSize={pageSize}
      onFiltersChange={(next) => {
        setCursor(undefined);
        setFilters(next);
      }}
      onClearScope={() => {
        setCursor(undefined);
        setSearchParams({});
      }}
      onPageSizeChange={(size) => {
        setCursor(undefined);
        setPageSize(size);
      }}
      onNextPage={() => {
        if (state.status === "ready" && state.data.nextCursor) {
          setCursor(state.data.nextCursor);
        }
      }}
      onFirstPage={() => setCursor(undefined)}
      isFirstPage={cursor === undefined}
      onRetry={() => setAttempt((value) => value + 1)}
    />
  );
}

async function loadUserDetail(userId: number, signal?: AbortSignal): Promise<SuperAdminUserDetailData> {
  const [user, sessions] = await Promise.all([
    getSuperAdminUser(userId, signal),
    listSuperAdminUserSessions(userId, signal),
  ]);
  return { user, sessions: sessions.items };
}

function UserDetailRoute({
  currentUserId,
  onLogout,
}: {
  currentUserId: number;
  onLogout: () => void;
}): React.JSX.Element {
  const params = useParams();
  const userId = Number(params.userId);
  const [state, setState] = useState<SuperAdminLoadState<SuperAdminUserDetailData>>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!Number.isInteger(userId) || userId <= 0) {
      setState({ status: "error" });
      return;
    }
    const controller = new AbortController();
    loadUserDetail(userId, controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: "ready", data });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [userId, attempt]);

  return (
    <SuperAdminUserDetailView
      state={state}
      onRetry={() => setAttempt((value) => value + 1)}
      onRevokeSession={async (session) => {
        await revokeSuperAdminUserSession(userId, session.id);
        if (session.current) {
          onLogout();
          return;
        }
        setState({ status: "ready", data: await loadUserDetail(userId) });
      }}
      onRevokeAll={async () => {
        await revokeAllSuperAdminUserSessions(userId);
        if (userId === currentUserId) {
          onLogout();
          return;
        }
        setState({ status: "ready", data: await loadUserDetail(userId) });
      }}
    />
  );
}

function TeamRoute({
  currentUserId,
  onOpenMobileNav,
}: {
  currentUserId: number;
  onOpenMobileNav: () => void;
}): React.JSX.Element {
  const [state, setState] = useState<
    SuperAdminLoadState<Awaited<ReturnType<typeof listSuperAdminPlatformAdmins>>["items"]>
  >({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    listSuperAdminPlatformAdmins(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: "ready", data: data.items });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [attempt]);

  return (
    <SuperAdminTeamSection
      state={state}
      currentUserId={currentUserId}
      onRetry={() => setAttempt((value) => value + 1)}
      onOpenMobileNav={onOpenMobileNav}
      onAdd={async (email) => {
        await addSuperAdminPlatformAdmin(email);
        setState({ status: "ready", data: (await listSuperAdminPlatformAdmins()).items });
      }}
      onRemove={async (admin) => {
        await removeSuperAdminPlatformAdmin(admin.id);
        if (admin.userId === currentUserId) {
          window.location.assign("/");
          return;
        }
        setState({ status: "ready", data: (await listSuperAdminPlatformAdmins()).items });
      }}
    />
  );
}

function SettingsRoute({ currentUserId }: { currentUserId: number }): React.JSX.Element {
  const [state, setState] = useState<SuperAdminLoadState<SuperAdminSettingsData>>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([getSuperAdminConfig(controller.signal), getSuperAdminSettings(controller.signal)]).then(
      ([config, settings]) => {
        if (!controller.signal.aborted) setState({ status: "ready", data: { config, settings } });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [attempt]);

  return (
    <SuperAdminSettingsView
      state={state}
      onRetry={() => setAttempt((value) => value + 1)}
      renderTeam={(openMobileNav) => <TeamRoute currentUserId={currentUserId} onOpenMobileNav={openMobileNav} />}
      onSave={async ({ config, settings }) => {
        if (state.status !== "ready") {
          return;
        }
        const nextConfig = Object.keys(config).length > 0 ? await updateSuperAdminConfig(config) : state.data.config;
        const nextSettings =
          Object.keys(settings).length > 0 ? await updateSuperAdminSettings(settings) : state.data.settings;
        setState({ status: "ready", data: { config: nextConfig, settings: nextSettings } });
      }}
    />
  );
}

function OrganizationDetailRoute(): React.JSX.Element {
  const params = useParams();
  const organizationId = Number(params.organizationId);
  const [state, setState] = useState<
    SuperAdminLoadState<Awaited<ReturnType<typeof getSuperAdminOrganization>>>
  >({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!Number.isInteger(organizationId) || organizationId <= 0) {
      setState({ status: "error" });
      return;
    }
    const controller = new AbortController();
    getSuperAdminOrganization(organizationId, controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: "ready", data });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [organizationId, attempt]);

  return (
    <SuperAdminOrganizationDetailView
      state={state}
      onRetry={() => setAttempt((value) => value + 1)}
      onChangeStatus={async (status) => {
        const data = await updateSuperAdminOrganizationStatus(organizationId, status);
        setState({ status: "ready", data });
      }}
      onChangeBeta={async (isBetaOrganization) => {
        const data = await updateSuperAdminOrganizationBeta(organizationId, isBetaOrganization);
        setState({ status: "ready", data });
      }}
      onSaveEntitlements={async (patch) => {
        await updateSuperAdminOrganizationEntitlement(organizationId, patch);
        const data = await getSuperAdminOrganization(organizationId);
        setState({ status: "ready", data });
      }}
    />
  );
}
